import axios from 'axios'

import { formatReadableDate } from '../helpers/dateHelpers'

const URL_BASE = '/api/v1/console/'
const URL_V3_BASE = '/api/v3/'

export enum RepaymentPolicyStatus {
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  TERMINATED = 'TERMINATED',
  DRAFT = 'DRAFT',
  FINISHED = 'FINISHED',
  STOP_BILLING_AND_COLLECTION = 'STOP_BILLING_AND_COLLECTION',
}

export const PolicyStatusText = {
  [RepaymentPolicyStatus.ACTIVE]: 'Active',
  [RepaymentPolicyStatus.PAUSED]: 'Collection paused',
  [RepaymentPolicyStatus.TERMINATED]: 'Cancelled',
  [RepaymentPolicyStatus.DRAFT]: 'Pending',
  [RepaymentPolicyStatus.FINISHED]: 'Finished',
  [RepaymentPolicyStatus.STOP_BILLING_AND_COLLECTION]: 'Billing & collection paused',
}

export const PolicyStatusSortValue = {
  [RepaymentPolicyStatus.ACTIVE]: 1,
  [RepaymentPolicyStatus.PAUSED]: 2,
  [RepaymentPolicyStatus.TERMINATED]: 5,
  [RepaymentPolicyStatus.DRAFT]: 0,
  [RepaymentPolicyStatus.FINISHED]: 4,
  [RepaymentPolicyStatus.STOP_BILLING_AND_COLLECTION]: 3,
}

export enum RepaymentScheduleType {
  MONTHLY = 'MONTHLY',
  WEEKLY = 'WEEKLY',
}

export const RepaymentScheduleTypeText = {
  [RepaymentScheduleType.MONTHLY]: 'Monthly',
  [RepaymentScheduleType.WEEKLY]: 'Weekly',
}

export interface RepaymentHistoryItem {
  payment_date: string
  payment_note: string
  payment_amount: number
  statement_id: string
  job_id: string
  job_human_id: string
}

export interface AssociatedEquipmentInfo {
  license_plate_number: string
  license_plate_state: string
  vin_number: string
}

export enum DeductionConfigStatus {
  SCHEDULED = 'SCHEDULED',
  ACTIVE = 'ACTIVE',
  FINISHED = 'FINISHED',
  CANCELED = 'CANCELED',
}

export interface DeductionConfig {
  id: string
  reason: DeductionCreditReason
  date_scheduled: string
  date_served: string
  update_note: string
  created_by_note: string
  status: DeductionConfigStatus
  num_periods: number | null
  advance_periodic_amount: string | null
  advance_total_billing_amount: string | null
  advance_accrued_billing_amount: string | null
  subscription_periodic_amount: string | null
  subscription_accrued_billing_amount: string | null
}

export interface Repayment {
  repayment_details: {
    amount_owed_per_period: string
    amount_owed_this_period: string
    amount_paid_this_period: string
    amount_paid: string
    current_period_start_date: string
    current_period_end_date: string
    equipment: AssociatedEquipmentInfo | null
    next_billing_date: string | null
    payment_period_type: RepaymentScheduleType
    payment_start_date: string
    payment_end_date: string
    policy_id: string
    policy_sub_header: string
    policy_description: string
    policy_type: VendorProgramPolicyType
    policy_status: RepaymentPolicyStatus
    remaining_payment: string
    remaining_payment_this_period: string
    terminate_reason: string | null
    has_pending_payments: boolean | null
    total_amount_owed: string
    total_debt_written_off: string
    underpayment_from_previous_periods: string
    underpayment_from_previous_periods_as_of_today: string
    vendor_program_tags: VendorProgram['name'][]
    deduction_configs: DeductionConfig[]
    team_id: string
    team_name: string
  }
  repayment_history: RepaymentHistoryItem[]
}

export enum RepaymentRiskTier {
  LOW = 'LOW',
  MODERATE = 'MODERATE',
  HIGH = 'HIGH',
}

export const RiskTierTooltipText: Record<any, string> = {
  [RepaymentRiskTier.LOW]:
    'Deduction rate is up to 25% per job. Weekly debt is less than 15% of average weekly revenue. Additional advances can be extended.',
  [RepaymentRiskTier.MODERATE]:
    'Deduction rate is up to 30% per job. Weekly debt is between 15–30% of average weekly revenue. Additional advances should only be extended after careful review.',
  [RepaymentRiskTier.HIGH]:
    'Deduction rate is up to 40% per job. Weekly debt is greater than 30% of average weekly revenue. Additional advances should not be extended.',
}

export const RiskTierText: Record<any, string> = {
  [RepaymentRiskTier.LOW]: 'Low risk',
  [RepaymentRiskTier.MODERATE]: 'Medium risk',
  [RepaymentRiskTier.HIGH]: 'High risk',
}

export interface RepaymentsBreakdowns {
  escrow_breakdown: {
    policy_type: 'CT_ESCROW'
    total_remaining_balance: string
    underpayment: string
    number_of_policies: number
  }
  advance_breakdown: {
    policy_type: VendorProgramPolicyType.ADVANCE
    total_remaining_balance: string
    underpayment: string
    number_of_policies: number
  }
  subscription_breakdown: {
    policy_type: VendorProgramPolicyType.SUBSCRIPTION
    total_remaining_balance: string
    underpayment: string
    number_of_policies: number
  }
  risk_tier: RepaymentRiskTier
  repayments: Repayment[]
}

export interface PolicyDocument {
  id: string
  policy: string
  policy_document_file: string
  readable_filename: string
}

export enum VendorProgramSubheaderType {
  TRAILER_ID = '[trailer id]',
  TEAM_NAME = '[team name]',
  VIN_NUMBER = '[associated VIN number]',
  PLATES_NUMBER = '[associated plates number]',
  DRIVER_NAME = '[associated driver name]',
  JOB_ID = 'Job ID [job id]',
  UNDERPAYMENT = 'Underpayment for [month, year]',
  PAID_BY_CT_ON_DATE = 'Paid by CT on [month abbreviation, day, full year]',
  DATE_INSTANCE_OCCURRED = '[Date instance occurred]',
  NOTHING_TO_FILL = '[Nothing to fill]',
}

export enum VendorProgramPolicyType {
  SUBSCRIPTION = 'Subscription',
  ADVANCE = 'Advance',
}

export const ONLY_COLLECTION_POLICY = 'JOB_PAYOUT'

export const ONLY_RECIPIENT_TYPE = 'External'

export interface VendorProgram {
  description: string
  name: string
  policy_type: VendorProgramPolicyType
  subheader: VendorProgramSubheaderType
  subheader_items: string[]
}

export interface TrucksAndTrailersChoice {
  id?: string
  name: string
  vin_number: string
  license_plate_number: string
}

export interface CreateUpdatePolicyPayload {
  policy_fields: {
    collection_policy: string
    description: string
    num_periods: number
    payment_start_date: string
    periodic_amount: number
    periodic_payment_interval: string
    status: string
    sub_header: string
    team_id: string
    vin_number?: string
  }
  recipient_type: string
  vendor_program: string
  policy_documents?: File[]
  terminate_reason?: string
}

export const DropdownConfig: Record<
  RepaymentPolicyStatus,
  {
    label: string
    dialogTitle: string
    dialogDescription: string
    cantBeUndone?: boolean
  }
> = {
  [RepaymentPolicyStatus.DRAFT]: {
    // no dropdown option for draft yet, to complete the RepaymentPolicyStatus enum keys
    label: '',
    dialogTitle: '',
    dialogDescription: '',
  },
  [RepaymentPolicyStatus.ACTIVE]: {
    label: 'Resume collection',
    dialogTitle: 'Resume collection?',
    dialogDescription: 'This will resume payment collection from the team’s job statements.',
  },
  [RepaymentPolicyStatus.PAUSED]: {
    label: 'Pause collection',
    dialogTitle: 'Pause collection?',
    dialogDescription:
      'This will pause payment collection from the team’s job statements. Billing will continue, and the total amount owed will continue to accrue.',
  },
  [RepaymentPolicyStatus.FINISHED]: {
    label: 'Finish policy',
    dialogTitle: 'Finish policy?',
    dialogDescription: 'This will mark the policy as finished and fully paid off.',
    cantBeUndone: true,
  },
  [RepaymentPolicyStatus.STOP_BILLING_AND_COLLECTION]: {
    label: 'Pause billing & collection',
    dialogTitle: 'Pause billing & collection?',
    dialogDescription: 'This will pause billing and payment collection from the team’s job statements.',
    cantBeUndone: false,
  },
  [RepaymentPolicyStatus.TERMINATED]: {
    label: 'Cancel policy',
    dialogTitle: 'Cancel policy?',
    dialogDescription: 'This will cancel the policy and remove any remaining amount owed to CloudTrucks.',
    cantBeUndone: true,
  },
}

export const ApplicablePolicyActions: Record<RepaymentPolicyStatus, RepaymentPolicyStatus[]> = {
  [RepaymentPolicyStatus.DRAFT]: [RepaymentPolicyStatus.ACTIVE, RepaymentPolicyStatus.TERMINATED],
  [RepaymentPolicyStatus.ACTIVE]: [
    RepaymentPolicyStatus.PAUSED,
    RepaymentPolicyStatus.FINISHED,
    RepaymentPolicyStatus.STOP_BILLING_AND_COLLECTION,
    RepaymentPolicyStatus.TERMINATED,
  ],
  [RepaymentPolicyStatus.PAUSED]: [
    RepaymentPolicyStatus.ACTIVE,
    RepaymentPolicyStatus.FINISHED,
    RepaymentPolicyStatus.STOP_BILLING_AND_COLLECTION,
    RepaymentPolicyStatus.TERMINATED,
  ],
  [RepaymentPolicyStatus.STOP_BILLING_AND_COLLECTION]: [
    RepaymentPolicyStatus.ACTIVE,
    RepaymentPolicyStatus.FINISHED,
    RepaymentPolicyStatus.TERMINATED,
  ],
  [RepaymentPolicyStatus.FINISHED]: [],
  [RepaymentPolicyStatus.TERMINATED]: [],
}

export const getPolicyDetails = (
  teamId: string,
  repayment_details: Repayment['repayment_details'],
  status: RepaymentPolicyStatus,
  paymentPeriods: number | 'N/A',
  amountOwedPerPeriod: number | 'N/A',
  updatedStatus: RepaymentPolicyStatus,
  terminateReason?: string
): CreateUpdatePolicyPayload => ({
  policy_fields: {
    collection_policy: ONLY_COLLECTION_POLICY,
    description: repayment_details.policy_description,
    num_periods:
      repayment_details.policy_type === VendorProgramPolicyType.SUBSCRIPTION
        ? -1
        : paymentPeriods === 'N/A'
        ? 1
        : paymentPeriods,
    payment_start_date: repayment_details.payment_start_date,
    periodic_amount: amountOwedPerPeriod === 'N/A' ? 0 : amountOwedPerPeriod, // TODO: @taylangocmen BE missing
    periodic_payment_interval: repayment_details.payment_period_type,
    status: updatedStatus,
    sub_header: repayment_details.policy_sub_header || 'Nothing to fill', // THIS IS SOMETIMES MISSING
    team_id: teamId,
    vin_number: repayment_details.equipment?.vin_number,
  },
  recipient_type: ONLY_RECIPIENT_TYPE,
  vendor_program: repayment_details.vendor_program_tags[0] ?? '',
  terminate_reason: terminateReason,
})

export const formatPeriodicAmount = (advanced_amount: string, number_of_payment_periods: string) =>
  Math.floor((parseFloat(advanced_amount) / Math.abs(parseFloat(number_of_payment_periods))) * 100) / 100

export const getNextMondayPlus24Hours = () => {
  // Ref: https://stackoverflow.com/a/33078673
  // This gets next Monday
  const today = new Date()
  // 1 -> Monday
  // Monday - Today's Day = distance from today to a Monday
  // +7 , %7 to get a positive number (i.e. next Monday)
  const offset = (1 - new Date().getDay() + 7) % 7
  // Policy start date has to be > 24h from now, so offset has to be > 1
  // Update date with today + offset, if offset is > current month's # of days
  // then the month is incremented automatically
  const nextMonday = new Date(new Date().setDate(today.getDate() + (offset > 1 ? offset : offset + 7)))
  // Policy start dates are always at 12AM (midnight)
  const nextMondayMidnight = new Date(nextMonday.setHours(0, 0, 0, 0))

  return nextMondayMidnight
}

export const getPaymentPeriodsFromPolicy = (policy: Repayment) => {
  return policy.repayment_details.total_amount_owed === 'N/A' ||
    policy.repayment_details.remaining_payment === 'N/A'
    ? 'N/A'
    : Math.ceil(
        parseFloat(policy.repayment_details.total_amount_owed) /
          parseFloat(policy.repayment_details.amount_owed_per_period)
      )
}

export const formatSubheaderValue = (subHeader: VendorProgramSubheaderType, value: string) => {
  switch (subHeader) {
    case VendorProgramSubheaderType.TRAILER_ID:
    case VendorProgramSubheaderType.TEAM_NAME:
    case VendorProgramSubheaderType.VIN_NUMBER:
    case VendorProgramSubheaderType.PLATES_NUMBER:
    case VendorProgramSubheaderType.DRIVER_NAME:
      return value
    case VendorProgramSubheaderType.JOB_ID:
      return `Job ID ${value}`
    case VendorProgramSubheaderType.UNDERPAYMENT:
      return `Underpayment for ${value ? formatReadableDate(value, { noDay: true }) : ''}`
    case VendorProgramSubheaderType.PAID_BY_CT_ON_DATE:
      return `Paid by CT on ${value ? formatReadableDate(value, { monthShort: true }) : ''}`
    case VendorProgramSubheaderType.DATE_INSTANCE_OCCURRED:
      return value ? formatReadableDate(value) : ''
    case VendorProgramSubheaderType.NOTHING_TO_FILL:
    default:
      return 'Nothing to fill'
  }
}

export const parseSubheaderValue = (subHeader: VendorProgramSubheaderType, value: string) => {
  switch (subHeader) {
    case VendorProgramSubheaderType.TRAILER_ID:
      return { trailer_id: value }
    case VendorProgramSubheaderType.TEAM_NAME:
      return { team_name: value }
    case VendorProgramSubheaderType.VIN_NUMBER:
      return { vin_number: value }
    case VendorProgramSubheaderType.PLATES_NUMBER:
      return { plates_number: value }
    case VendorProgramSubheaderType.DRIVER_NAME:
      return { driver_name: value }
    case VendorProgramSubheaderType.JOB_ID:
      return { job_id: value.startsWith('Job ID ') ? value.replace('Job ID ', '').trim() : value.trim() }
    case VendorProgramSubheaderType.UNDERPAYMENT:
      return {
        underpayment_date: value.startsWith('Underpayment for ')
          ? new Date(value.replace('Paid by CT on ', '').trim())
          : '',
      }
    case VendorProgramSubheaderType.PAID_BY_CT_ON_DATE:
      return {
        paid_by_ct_on_date: value.startsWith('Paid by CT on ')
          ? new Date(value.replace('Paid by CT on ', '').trim())
          : '',
      }
    case VendorProgramSubheaderType.DATE_INSTANCE_OCCURRED:
      return { date_instance_occurred: new Date(value) }
    case VendorProgramSubheaderType.NOTHING_TO_FILL:
    default:
      return {}
  }
}

export function getRepaymentsBreakdowns({ teamId, vendorId }: { teamId?: string; vendorId?: string }) {
  return axios
    .get(
      `${URL_V3_BASE}repayments/get_repayments/${teamId ? `?team_id=${teamId}` : `?vendor_id=${vendorId}`}`
    )
    .then((response) => {
      const data = response.data

      if (data.repayments) {
        data.repayments = (data.repayments as any).sort(
          (a: any, b: any) =>
            PolicyStatusSortValue[a.repayment_details.policy_status as RepaymentPolicyStatus] -
            PolicyStatusSortValue[b.repayment_details.policy_status as RepaymentPolicyStatus]
        )
      }

      return data as RepaymentsBreakdowns
    })
}

export function getRepaymentForPolicy({
  teamId,
  vendorId,
  policyId,
}: {
  teamId?: string
  policyId?: string
  vendorId?: string
}) {
  return axios
    .get(
      `${URL_V3_BASE}repayments/get_repayments/${teamId ? `?team_id=${teamId}` : ''}${
        vendorId ? `?vendor_id=${vendorId}` : ''
      }&policy_id=${policyId}`
    )
    .then((response) => {
      return response.data as Repayment
    })
}

export function getVendorPrograms(teamId: string) {
  return axios.get(`${URL_BASE}vendor-programs/?team_id=${teamId}`).then((response) => {
    return response.data as VendorProgram[]
  })
}

export function createRepaymentPolicy(payload: CreateUpdatePolicyPayload) {
  return axios.post(`${URL_V3_BASE}repayments/create-payment-policy/`, payload).then((response) => {
    return response.data as { policy_id: string }
  })
}

export function updateRepaymentPolicy(policyId: string, payload: CreateUpdatePolicyPayload) {
  return axios
    .post(`${URL_V3_BASE}repayments/${policyId}/update-payment-policy/`, payload)
    .then((response) => {
      return response.data as { policy_id: string }
    })
}

export function getPolicyDocuments(policyId: string | undefined) {
  return axios
    .get(`${URL_V3_BASE}policy-document/get-policy-documents/?policy_id=${policyId}`)
    .then((response) => {
      return response.data as PolicyDocument[]
    })
}

export function createPolicyDocument(policyId: string, policy_document_file: string | Blob) {
  const formData = new FormData()
  formData.append('policy_id', policyId)
  formData.append('policy_document', policy_document_file)

  return axios
    .post(`${URL_V3_BASE}policy-document/upload-policy-document/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((response) => {
      return response
    })
}

export function deletePolicyDocument(policyDocumentId: string) {
  return axios.post(`${URL_V3_BASE}policy-document/delete-policy-document/`, {
    policy_document_id: policyDocumentId,
  })
}

export enum DeductionCreditReason {
  CORRECTION = 'CORRECTION',
  DEBT_WRITE_OFF = 'DEBT_WRITE_OFF',
  EXTERNAL_PAYMENT = 'EXTERNAL_PAYMENT',
  REFINANCE = 'REFINANCE',
  DEFAULT = 'DEFAULT',
  OTHER = 'OTHER',
}

export interface DeductionCreditPayload {
  reason: DeductionCreditReason
  amount?: string
  date_scheduled?: string
  periodic_amount?: string
  total_billing_amount?: string // for either subscription or advance
  notes?: string // TODO: BE missing
}

export function createDeductionCredit(payload: DeductionCreditPayload, policyId: string | undefined) {
  return axios.post(`${URL_V3_BASE}repayments/${policyId}/update-deductions/`, payload)
}

export function cancelDeductionCredit(deductionConfigId: string) {
  return axios.post(`${URL_V3_BASE}deduction-configs/${deductionConfigId}/cancel/`)
}

export function sendVendorReport(email: string) {
  return axios.post(`${URL_V3_BASE}repayments/send-vendor-report/`, { email })
}
